import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { comics, categories } from '@/mocks/comics';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === slug),
    [slug]
  );

  const filteredComics = useMemo(
    () =>
      slug
        ? comics.filter((comic) => comic.categories.includes(slug))
        : comics,
    [slug]
  );

  const categoryName = currentCategory?.name || '全部分类';
  const categoryDesc = currentCategory
    ? `浏览${currentCategory.name}类型的韩国漫画，好看的${currentCategory.name}韩漫推荐，韩国漫画网站下拉式免费阅读。`
    : '浏览全部类型的韩国漫画，好看的韩国漫画大全，韩国漫画网站下拉式免费阅读。';

  return (
    <>
      <SchemaOrg type="WebSite" />
      <SchemaOrg type="Organization" />
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${currentCategory ? currentCategory.name + ' - ' : ''}韩漫画分类 - 韩国漫画网站`,
          description: categoryDesc,
          url: `https://www.hanmanhua.com/category/${slug || ''}`,
          inLanguage: 'zh-CN',
        }}
      />

      <Navbar />

      <main className="pt-16">
        <div className="w-full bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryName },
              ]}
            />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
              {categoryName}
            </h1>
            <p className="mt-2 text-sm text-foreground-500">{categoryDesc}</p>
          </div>

          {/* Category Tabs */}
          <div className="mb-8">
            <div className="flex flex-wrap gap-2">
              <Link
                to="/category/langman"
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  !slug || slug === 'langman'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                全部
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>

          {filteredComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5" data-product-shop="">
              {filteredComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类下暂无漫画，请浏览其他分类</p>
              <Link
                to="/category/langman"
                className="mt-4 inline-flex px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
              >
                查看全部漫画
              </Link>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}